import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Summary.css';
import { getAssetTypes, getPinnedAssetSummary, getSitesLite, getPlantsBySite, AssetSummaryItem, AlertItem, getGatewayStatus, parsePaginatedSummaryResponse, PaginatedAssetSummaryResponse } from '../../Services/serviceURL';
import { CustomerLite } from '../Plants/CreatePlant';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchAssetSummary, setPinnedAssetsMapByCustomer, PinnedAssetsMap, pinAsset, loadPinnedAssetsFromStorage, buildSummaryCacheKey, loadAssetSummaryFromCache, clearAssetSummaryPageCache } from './SummarySlice';
import useSearch from '../../hooks/useSearch';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../store';
import { refershTokenApi } from '../../Services/Users.service';
import { SortableKeys } from './summaryTypes';
import { sortSummaryRows } from './summarySortUtils';
import { downloadAssetsCsv } from './summaryDownloadUtils';
import { fetchSummaryCustomers } from './summaryCustomerUtils';
import { extractUserInfoFromToken } from './summaryAuthUtils';
import { mergeSitesFromAssets, mergePlantsFromAssets, ExtractedSite, ExtractedPlant } from './summarySitePlantUtils';
import {
  countPinnedAssetsInMap,
  filterRowsByAssetType,
  filterRowsByAlertsOnly,
  filterPinnedRowsByDropdowns,
  filterPinnedRowsBySearch,
  isSummaryDownloadDisabled,
  getNextSortDirection,
} from './summaryRowFilterUtils';
import { applySiteSelection, applyPlantSelection } from './summarySelectionHandlers';
import SummaryPageView from './SummaryPageView';

export function useSummaryPage() {
  const navigate = useNavigate();
  
  // Get user details from auth state (similar to UserPreferences.tsx)
  const userId = useSelector((state: RootState) => state.auth.userId);
  const userRole = useSelector((state: RootState) => state.auth.userRole);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  
  const tokenUserInfo = extractUserInfoFromToken();
  const [extractedSites, setExtractedSites] = useState<ExtractedSite[]>([]);

  const [extractedPlants, setExtractedPlants] = useState<ExtractedPlant[]>([]);
 

  // Get the current user ID for pin/unpin operations
  const getCurrentUserId = (): string => {
    // Try multiple sources for user ID (similar to UserPreferences.tsx)
    const userIdToUse = tokenUserInfo?.userId || 
                       tokenUserInfo?.email || 
                       userId || 
                       localStorage.getItem('loginemail') || 
                       localStorage.getItem('userId') ||
                       'unknown-user'; // fallback
    
    return userIdToUse;
  };
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [sites, setSites] = useState<{ siteId: string; site: string }[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedPlantId, setSelectedPlantId] = useState<string>('');
  const [assetTypes, setAssetTypes] = useState<any[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKeys; direction: 'asc' | 'desc' } | null>(null);
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pinnedPageItems, setPinnedPageItems] = useState<any[]>([]);
  const [pinnedTotalCount, setPinnedTotalCount] = useState(0);
  const [pinnedTotalPages, setPinnedTotalPages] = useState(0);
  const [pinnedPageLoading, setPinnedPageLoading] = useState(false);
  const [pinnedInitDone, setPinnedInitDone] = useState(false);
  const [activeTab, setActiveTab] = useState<'assets' | 'pinned' | 'mapview'>('assets');
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showGatewayStatusModal, setShowGatewayStatusModal] = useState(false);
  const [clientFilteredAssets, setClientFilteredAssets] = useState<any[]>([]);
  const [selectedAssetAlerts, setSelectedAssetAlerts] = useState<{
    assetName: string;
    alerts: AlertItem[];
  } | null>(null);
  const [selectedGatewayStatus, setSelectedGatewayStatus] = useState<{
    assetName: string;
    status: string;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const pinnedAssetsMapByCustomer = useSelector(
    (state: RootState) => state.summary.pinnedAssetsMapByCustomer
  );

  const LOCAL_STORAGE_KEY = 'pinnedAssetsMapByCustomer';

  const pinnedAssetsMap = useMemo(() => {
    if (selectedCustomerId) {
      return pinnedAssetsMapByCustomer[selectedCustomerId] || {};
    } else {
      const merged: PinnedAssetsMap = {};
      Object.values(pinnedAssetsMapByCustomer).forEach((customerMap) => {
        Object.assign(merged, customerMap);

      });
      return merged;
    }
  }, [selectedCustomerId, pinnedAssetsMapByCustomer]);
  const dispatch = useAppDispatch();
  const plants = useAppSelector((state) => state.summary.plants);
  const assetSummary = useAppSelector((state) => state.summary.assetSummary);
  const totalCount = useAppSelector((state) => state.summary.totalCount);
  const loading = useAppSelector((state) => state.summary.loading);
  const [pinningAssetId, setPinningAssetId] = useState<string | null>(null);
  const prevFilterKeyRef = useRef('');
  const pinnedPageCacheRef = useRef<Record<string, PaginatedAssetSummaryResponse>>({});
  const pinnedInitDoneRef = useRef(false);
  const sitesLiteCacheRef = useRef<Record<string, ExtractedSite[]>>({});
  const plantsBySiteCacheRef = useRef<Record<string, ExtractedPlant[]>>({});
  const customersRef = useRef(customers);
  customersRef.current = customers;
  const extractedSitesRef = useRef(extractedSites);
  extractedSitesRef.current = extractedSites;


// Load sites for a selected customer; otherwise derive from loaded summary rows
useEffect(() => {
  if (!selectedCustomerId) return;

  const cached = sitesLiteCacheRef.current[selectedCustomerId];
  if (cached) {
    setExtractedSites(cached);
    return;
  }

  const loadSitesForCustomer = async () => {
    try {
      const response = await getSitesLite(selectedCustomerId);
      const siteData = (response as { Data?: unknown })?.Data ?? response;
      const siteList = Array.isArray(siteData) ? siteData : [];
      const customer = customersRef.current.find((c) => c.customerId === selectedCustomerId);
      const mapped = siteList.map((site: { siteId: string; site?: string; siteName?: string }) => ({
        siteId: site.siteId,
        site: site.site || site.siteName || '',
        customerId: selectedCustomerId,
        customerName: customer?.customerName || '',
      }));

      sitesLiteCacheRef.current[selectedCustomerId] = mapped;
      setExtractedSites(mapped);
    } catch {
      setExtractedSites([]);
    }
  };

  loadSitesForCustomer();
}, [selectedCustomerId]);

// Backfill customer names on cached sites once the customer list finishes loading
useEffect(() => {
  if (!selectedCustomerId || customers.length === 0) return;

  const customer = customers.find((c) => c.customerId === selectedCustomerId);
  if (!customer?.customerName) return;

  setExtractedSites((prev) => {
    const needsUpdate = prev.some(
      (site) => site.customerId === selectedCustomerId && site.customerName !== customer.customerName
    );
    if (!needsUpdate) return prev;

    const updated = prev.map((site) =>
      site.customerId === selectedCustomerId
        ? { ...site, customerName: customer.customerName }
        : site
    );
    sitesLiteCacheRef.current[selectedCustomerId] = updated;
    return updated;
  });
}, [customers, selectedCustomerId]);

// Merge site/plant options from the currently loaded summary page when no customer is selected
useEffect(() => {
  if (selectedCustomerId || !Array.isArray(assetSummary) || assetSummary.length === 0) return;

  setExtractedSites((prev) => mergeSitesFromAssets(assetSummary, prev));
  setExtractedPlants((prev) => mergePlantsFromAssets(assetSummary, prev));
}, [assetSummary, selectedCustomerId]);

// Derive plant options from summary when a customer is selected but no site is selected
useEffect(() => {
  if (selectedSiteId) return;

  if (selectedCustomerId) {
    setExtractedPlants((prev) =>
      mergePlantsFromAssets(
        assetSummary.filter((asset) => asset.customerId === selectedCustomerId),
        prev
      )
    );
  } else {
    setExtractedPlants([]);
  }
}, [selectedSiteId, selectedCustomerId, assetSummary]);

// Load plants when a site is selected (API call only when siteId changes; cached by siteId)
useEffect(() => {
  if (!selectedSiteId) return;

  const cached = plantsBySiteCacheRef.current[selectedSiteId];
  if (cached) {
    setExtractedPlants(cached);
    return;
  }

  const loadPlantsForSite = async () => {
    try {
      const resp = await getPlantsBySite(selectedSiteId);
      const plantData = resp.data?.Data ?? [];
      const site = extractedSitesRef.current.find((s) => s.siteId === selectedSiteId);
      const customer = customersRef.current.find((c) => c.customerId === selectedCustomerId);
      const siteInfo: ExtractedSite = site || {
        siteId: selectedSiteId,
        site: '',
        customerId: selectedCustomerId,
        customerName: customer?.customerName || '',
      };

      const mapped = (Array.isArray(plantData) ? plantData : []).map(
        (plant: { plantId?: string; id?: string; plantName?: string; name?: string }) => ({
          plantId: plant.plantId || plant.id || '',
          plantName: plant.plantName || plant.name || '',
          siteId: siteInfo.siteId,
          siteName: siteInfo.site,
          customerId: siteInfo.customerId,
          customerName: siteInfo.customerName,
        })
      );

      plantsBySiteCacheRef.current[selectedSiteId] = mapped;
      setExtractedPlants(mapped);
    } catch {
      setExtractedPlants([]);
    }
  };

  loadPlantsForSite();
}, [selectedSiteId, selectedCustomerId]);

  // Refresh token on component mount
  useEffect(() => {
    const initializeTokenRefresh = async () => {
      try {
        const result = await refershTokenApi();
      } catch (error) {
        console.error("Summary: Error during token refresh:", error);
      }
    };

    initializeTokenRefresh();
  }, []);

  useEffect(() => {
    if (!Array.isArray(assetSummary)) {
      setClientFilteredAssets([]);
      return;
    }
    setClientFilteredAssets([...assetSummary]);
  }, [assetSummary]);
  useEffect(() => {
    fetchSummaryCustomers()
      .then((uniqueCustomers) => setCustomers(uniqueCustomers))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

 

useEffect(() => {
  if (activeTab !== 'assets') return;

  const filterKey = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  const filtersChanged = prevFilterKeyRef.current !== filterKey && prevFilterKeyRef.current !== '';
  prevFilterKeyRef.current = filterKey;

  const filters = {
    pageNumber: 1,
    pageSize: itemsPerPage,
    ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
    ...(selectedSiteId ? { siteId: selectedSiteId } : {}),
    ...(selectedPlantId ? { plantId: selectedPlantId } : {}),
  };

  const cacheKey = buildSummaryCacheKey('assets', filters);

  if (filtersChanged) {
    dispatch(clearAssetSummaryPageCache());
    dispatch(fetchAssetSummary(filters));
    return;
  }

  const cached = store.getState().summary.assetSummaryPageCache[cacheKey];
  if (cached) {
    dispatch(loadAssetSummaryFromCache(cacheKey));
  } else {
    dispatch(fetchAssetSummary(filters));
  }
}, [selectedCustomerId, selectedSiteId, selectedPlantId, activeTab, dispatch]);

const syncPinnedItemsToMap = useCallback((items: AssetSummaryItem[]) => {
  if (items.length === 0) return;

  const existingByCustomer = store.getState().summary.pinnedAssetsMapByCustomer;
  const updatesByCustomer: Record<string, PinnedAssetsMap> = {};

  items.forEach((asset) => {
    const customerId = asset.customerId;
    const assetId = asset.assetId || asset.id;
    if (!customerId || !assetId) return;

    if (!updatesByCustomer[customerId]) {
      updatesByCustomer[customerId] = { ...(existingByCustomer[customerId] || {}) };
    }
    updatesByCustomer[customerId][assetId] = {
      ...asset,
      assetId,
      pinnedAssetId: assetId,
    };
  });

  Object.entries(updatesByCustomer).forEach(([customerId, pinnedAssets]) => {
    dispatch(setPinnedAssetsMapByCustomer({ customerId, pinnedAssets }));
  });
}, [dispatch]);

// Refresh Pinned tab when Assets tab is refreshed to get the latest data
const mergeFreshDataIntoPinned = useCallback((items: AssetSummaryItem[]) => {
  if (!Array.isArray(items) || items.length === 0) return;

  const freshById: Record<string, AssetSummaryItem> = {};
  items.forEach((item) => {
    const assetId = item.assetId || (item as { id?: string }).id;
    if (assetId) freshById[assetId] = item;
  });

  if (Object.keys(freshById).length === 0) return;

  // Refresh rows already loaded on the Pinned tab table
  setPinnedPageItems((prev) => {
    if (!Array.isArray(prev) || prev.length === 0) return prev;
    let changed = false;
    const updated = prev.map((row) => {
      const assetId = row.assetId || row.id;
      const fresh = assetId ? freshById[assetId] : undefined;
      if (!fresh) return row;
      changed = true;
      return { ...row, ...fresh, assetId, pinnedAssetId: row.pinnedAssetId || assetId };
    });
    return changed ? updated : prev;
  });

  // Refresh the pinned map for existing pinned entries only
  // never add/remove pins, only update fields on assets that are already pinned.
  const existingByCustomer = store.getState().summary.pinnedAssetsMapByCustomer;
  const updatesByCustomer: Record<string, PinnedAssetsMap> = {};

  Object.entries(existingByCustomer).forEach(([customerId, pinnedMap]) => {
    Object.keys(pinnedMap).forEach((assetId) => {
      const fresh = freshById[assetId];
      if (!fresh) return;
      if (!updatesByCustomer[customerId]) {
        updatesByCustomer[customerId] = { ...pinnedMap };
      }
      updatesByCustomer[customerId][assetId] = {
        ...pinnedMap[assetId],
        ...fresh,
        assetId,
        pinnedAssetId: assetId,
      };
    });
  });

  Object.entries(updatesByCustomer).forEach(([customerId, pinnedAssets]) => {
    dispatch(setPinnedAssetsMapByCustomer({ customerId, pinnedAssets }));
  });
}, [dispatch]);

// Whenever the Assets tab summary data is (re)fetched, sync any newer field values
// (Last Data Received) into pinned assets so the Pinned tab
// stays in sync instead of showing the data as of pin time.
useEffect(() => {
  mergeFreshDataIntoPinned(assetSummary);
}, [assetSummary, mergeFreshDataIntoPinned]);

const syncPinnedFromApi = useCallback(async (
  customerId?: string,
  siteId?: string,
  plantId?: string
) => {
  try {
    let pageNumber = 1;
    let totalPages = 1;
    let totalCount = 0;
    const allPinned: AssetSummaryItem[] = [];

    do {
      const resp = await getPinnedAssetSummary(pageNumber, 500, customerId, siteId, plantId);
      const parsed = parsePaginatedSummaryResponse(resp.data);
      totalCount = parsed.totalCount;
      allPinned.push(...parsed.items);
      totalPages = parsed.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    setPinnedTotalCount(totalCount);
    setPinnedTotalPages(1);
    setPinnedPageItems(allPinned);

    const customerMap: Record<string, PinnedAssetsMap> = {};
    allPinned.forEach((asset) => {
      const cid = asset.customerId;
      const assetId = asset.assetId || asset.id;
      if (!cid || !assetId) return;
      if (!customerMap[cid]) customerMap[cid] = {};
      customerMap[cid][assetId] = { ...asset, assetId, pinnedAssetId: assetId };
    });

    const existingByCustomer = store.getState().summary.pinnedAssetsMapByCustomer;

    if (!customerId) {
      const allCustomerIds = new Set([
        ...Object.keys(existingByCustomer),
        ...Object.keys(customerMap),
      ]);
      allCustomerIds.forEach((cid) => {
        dispatch(setPinnedAssetsMapByCustomer({
          customerId: cid,
          pinnedAssets: customerMap[cid] || {},
        }));
      });
    } else {
      dispatch(setPinnedAssetsMapByCustomer({
        customerId,
        pinnedAssets: customerMap[customerId] || {},
      }));
    }
  } catch {
    // Keep localStorage / existing count on failure
  }
}, [dispatch]);

const fetchPinnedCount = useCallback(async () => {
  try {
    const resp = await getPinnedAssetSummary(
      1,
      1,
      selectedCustomerId || undefined,
      selectedSiteId || undefined,
      selectedPlantId || undefined
    );
    const parsed = parsePaginatedSummaryResponse(resp.data);
    setPinnedTotalCount(parsed.totalCount);
  } catch {
    // Preserve existing count when the count-only request fails
  }
}, [selectedCustomerId, selectedSiteId, selectedPlantId]);

const fetchPinnedPageData = useCallback(async (options?: { forceRefresh?: boolean }) => {
  const cacheKey = buildSummaryCacheKey('pinned', {
    pageNumber: 1,
    pageSize: itemsPerPage,
    customerId: selectedCustomerId || undefined,
    siteId: selectedSiteId || undefined,
    plantId: selectedPlantId || undefined,
  });

  if (options?.forceRefresh) {
    delete pinnedPageCacheRef.current[cacheKey];
  } else {
    const cached = pinnedPageCacheRef.current[cacheKey];
    if (cached) {
      setPinnedPageItems(cached.items);
      setPinnedTotalCount(cached.totalCount);
      setPinnedTotalPages(cached.totalPages);
      return;
    }
  }

  setPinnedPageLoading(true);
  try {
    const fetchPageSize = 500;
    let pageNumber = 1;
    let totalPages = 1;
    let totalCount = 0;
    const allItems: AssetSummaryItem[] = [];

    do {
      const resp = await getPinnedAssetSummary(
        pageNumber,
        fetchPageSize,
        selectedCustomerId || undefined,
        selectedSiteId || undefined,
        selectedPlantId || undefined
      );
      const parsed = parsePaginatedSummaryResponse(resp.data);
      allItems.push(...parsed.items);
      totalCount = parsed.totalCount;
      totalPages = parsed.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    const aggregated: PaginatedAssetSummaryResponse = {
      items: allItems,
      totalCount,
      pageNumber: 1,
      pageSize: allItems.length,
      totalPages: 1,
    };

    pinnedPageCacheRef.current[cacheKey] = aggregated;
    setPinnedPageItems(aggregated.items);
    setPinnedTotalCount(aggregated.totalCount);
    setPinnedTotalPages(aggregated.totalPages);
    syncPinnedItemsToMap(aggregated.items);
  } catch {
    setPinnedPageItems([]);
    setPinnedTotalPages(0);
  } finally {
    setPinnedPageLoading(false);
  }
}, [itemsPerPage, selectedCustomerId, selectedSiteId, selectedPlantId, syncPinnedItemsToMap]);

const pinnedFilterKeyRef = useRef('');
const prevActiveTabRef = useRef(activeTab);
const prevPinnedFiltersForCountRef = useRef('');

// Initialize pinned state: localStorage first, then API after token refresh
useEffect(() => {
  if (!isLoggedIn) return;

  let cancelled = false;
  pinnedInitDoneRef.current = false;
  setPinnedInitDone(false);

  const initializePinned = async () => {
    dispatch(loadPinnedAssetsFromStorage());

    const storedPinnedAssets = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    const interimCount = countPinnedAssetsInMap(storedPinnedAssets);
    if (interimCount > 0) {
      setPinnedTotalCount(interimCount);
    }

    try {
      await refershTokenApi();
    } catch (error) {
      console.error('Summary: Error during token refresh:', error);
    }

    if (cancelled) return;

    await syncPinnedFromApi();
    pinnedInitDoneRef.current = true;
    setPinnedInitDone(true);
    prevPinnedFiltersForCountRef.current = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  };

  initializePinned();

  return () => {
    cancelled = true;
  };
}, [isLoggedIn, dispatch, syncPinnedFromApi]);

// Update pinned badge count when filters change (after initial sync)
useEffect(() => {
  if (!isLoggedIn || activeTab === 'pinned' || !pinnedInitDone) return;

  const filterKey = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  if (prevPinnedFiltersForCountRef.current === filterKey) return;
  prevPinnedFiltersForCountRef.current = filterKey;

  fetchPinnedCount();
}, [isLoggedIn, activeTab, pinnedInitDone, fetchPinnedCount, selectedCustomerId, selectedSiteId, selectedPlantId]);

// Full pinned table data when pinned tab is active (pageSize 10 — items + totalCount)
useEffect(() => {
  if (!isLoggedIn || activeTab !== 'pinned' || !pinnedInitDone) {
    if (activeTab !== 'pinned') prevActiveTabRef.current = activeTab;
    return;
  }

  const justOpenedPinnedTab = prevActiveTabRef.current !== 'pinned';
  prevActiveTabRef.current = activeTab;

  const filterKey = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  const filtersChanged = pinnedFilterKeyRef.current !== filterKey && pinnedFilterKeyRef.current !== '';
  pinnedFilterKeyRef.current = filterKey;

  if (filtersChanged || justOpenedPinnedTab) {
    pinnedPageCacheRef.current = {};
  }
  fetchPinnedPageData({ forceRefresh: justOpenedPinnedTab || filtersChanged });
}, [isLoggedIn, activeTab, pinnedInitDone, fetchPinnedPageData, selectedCustomerId, selectedSiteId, selectedPlantId]);

  // Fetch asset types on mount
  useEffect(() => {
    const fetchAssetTypes = async () => {
      try {
        const response = await getAssetTypes();
        // Adjust if your API returns { Data: [...] }
        setAssetTypes(response.Data || []);
      } catch (err) {
        setAssetTypes([]);
      }
    };
    fetchAssetTypes();
  }, []);

const handleSiteChange = (siteId: string) => {
  const { siteId: nextSiteId, customerId } = applySiteSelection(
    siteId,
    extractedSites,
    selectedCustomerId
  );
  setSelectedSiteId(nextSiteId);
  setSelectedPlantId('');
  setSelectedCustomerId(customerId);
  setSelectedAssetType('');
  setCurrentPage(1);
};

const handlePlantChange = (plantId: string) => {
  const selection = applyPlantSelection(
    plantId,
    extractedPlants,
    selectedCustomerId,
    selectedSiteId
  );
  setSelectedPlantId(selection.plantId);
  setSelectedCustomerId(selection.customerId);
  setSelectedSiteId(selection.siteId);
  setSelectedAssetType('');
  setCurrentPage(1);
};


  // Handler for alerts toggle change
  const handleAlertsToggleChange = () => {
    setShowAlertsOnly(v => !v);
    setCurrentPage(1); // Reset pagination when filter changes
  };

  // Handler for alerts modal
  const handleAlertsClick = (asset: any) => {
    // Use the alerts data from the asset object
    const alertsData = asset.alerts || [];

    setSelectedAssetAlerts({
      assetName: asset.assetName || 'Unknown Asset',
      alerts: alertsData as AlertItem[]
    });
    setShowAlertsModal(true);
  };

  const closeAlertsModal = () => {
    setShowAlertsModal(false);
    setSelectedAssetAlerts(null);
  };

  const handleGatewayStatusClick = async (asset: { assetName?: string; gatewayDeviceId?: string }) => {
    const assetName = asset.assetName || 'Unknown Asset';

    setSelectedGatewayStatus({
      assetName,
      status: '',
      loading: true,
      error: null,
    });
    setShowGatewayStatusModal(true);

    if (!asset.gatewayDeviceId) {
      setSelectedGatewayStatus({
        assetName,
        status: '',
        loading: false,
        error: 'No gateway device configured for this asset.',
      });
      return;
    }

    const status = await getGatewayStatus(asset.gatewayDeviceId);
    setSelectedGatewayStatus({
      assetName,
      status,
      loading: false,
      error: status ? null : 'Unable to retrieve gateway status.',
    });
  };

  const closeGatewayStatusModal = () => {
    setShowGatewayStatusModal(false);
    setSelectedGatewayStatus(null);
  };

  type AssetRow = Record<string, any> & { alerts?: string[] };

  // Table rows from API page data; fall back to pin map only when page items are empty
  const pinnedRows = React.useMemo(() => {
    const source =
      pinnedPageItems.length > 0
        ? pinnedPageItems
        : activeTab === 'pinned'
          ? Object.values(pinnedAssetsMap)
          : [];

    return source.map((row: any) => ({
      ...row,
      assetId: row.assetId || row.id,
      isPinned: true,
      alerts: row.alerts || [],
    }));
  }, [pinnedPageItems, pinnedAssetsMap, activeTab]);

  // First, filter by asset type

  // Update all places that use assetSummary to use clientFilteredAssets instead
  const assetSummaryWithAlerts = React.useMemo(() => {
    function isPlainObject(value: unknown): value is AssetRow {
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    }
    
    // Use clientFilteredAssets instead of assetSummary
    if (!Array.isArray(clientFilteredAssets) || clientFilteredAssets.length === 0) return [];

    return clientFilteredAssets.map((row, idx) => {
      if (isPlainObject(row)) {
        const typedRow = row as AssetRow;
        const assetId = row.assetId as string;
        const isPinned = Boolean(pinnedAssetsMap[assetId]);

        return {
          ...typedRow,
          isPinned,
          alerts: row.alerts || [],
        };
      }
      return row;
    });
  }, [clientFilteredAssets, pinnedAssetsMap]);
  const assetTypeFiltered = React.useMemo(
    () => filterRowsByAssetType(assetSummaryWithAlerts, selectedAssetType),
    [assetSummaryWithAlerts, selectedAssetType]
  );

  const alertsFiltered = React.useMemo(
    () => filterRowsByAlertsOnly(assetTypeFiltered, showAlertsOnly),
    [assetTypeFiltered, showAlertsOnly]
  );

  // Then, use the search hook on the filtered data
  const { searchText, filteredData: filteredAssetSummary, handleSearchChange } = useSearch(alertsFiltered);

  // Apply dropdown filters to pinned rows
  const dropdownFilteredPinnedRows = React.useMemo(
    () =>
      filterPinnedRowsByDropdowns(pinnedRows, {
        selectedCustomerId,
        selectedSiteId,
        selectedPlantId,
        selectedAssetType,
        showAlertsOnly,
      }),
    [pinnedRows, selectedCustomerId, selectedSiteId, selectedPlantId, selectedAssetType, showAlertsOnly]
  );

  const filteredPinnedRows = React.useMemo(
    () => filterPinnedRowsBySearch(dropdownFilteredPinnedRows, searchText),
    [dropdownFilteredPinnedRows, searchText]
  );

  const [pinnedSortConfig, setPinnedSortConfig] = useState<{
    key: SortableKeys;
    direction: 'asc' | 'desc';
  } | null>(null);

  const requestSort = (key: SortableKeys) => {
    if (activeTab === 'pinned') {
      setPinnedSortConfig({
        key,
        direction: getNextSortDirection(pinnedSortConfig, key),
      });
      setSortConfig(null);
      return;
    }
    setSortConfig({
      key,
      direction: getNextSortDirection(sortConfig, key),
    });
    setPinnedSortConfig(null);
  };

  const sortedAssetSummary = React.useMemo(
    () => sortSummaryRows(filteredAssetSummary, sortConfig),
    [filteredAssetSummary, sortConfig]
  );

  const sortedPinnedRows = React.useMemo(
    () => sortSummaryRows(filteredPinnedRows, pinnedSortConfig),
    [filteredPinnedRows, pinnedSortConfig]
  );


  const totalItems = React.useMemo(() => {
    if (activeTab === 'mapview') return 0;
    if (activeTab === 'assets') return filteredAssetSummary.length;
    return filteredPinnedRows.length;
  }, [activeTab, filteredAssetSummary.length, filteredPinnedRows.length]);

  const totalPages = React.useMemo(() => {
    if (activeTab === 'mapview') return 0;
    return Math.max(1, Math.ceil(totalItems / itemsPerPage));
  }, [activeTab, totalItems, itemsPerPage]);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages && totalPages > 0) {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }
  };

  // Slice the fully filtered + sorted data set into the current page.
  const pagedAssetSummary = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedAssetSummary.slice(start, start + itemsPerPage);
  }, [sortedAssetSummary, currentPage, itemsPerPage]);

  const pagedPinnedRows = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedPinnedRows.slice(start, start + itemsPerPage);
  }, [sortedPinnedRows, currentPage, itemsPerPage]);

  // Keep the current page in range whenever filtering/searching shrinks the result set.
  useEffect(() => {
    if (activeTab === 'mapview') return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [activeTab, currentPage, totalPages]);

  const handlePinClick = async (asset: any) => {
    const assetId = asset.assetId;

    //  Determine the actual customer ID for this asset
    const customerIdToUpdate = selectedCustomerId || asset.customerId;

    if (!customerIdToUpdate) {
      return;
    }

    //  Get the correct pinned map for that customer
    const currentCustomerMap = pinnedAssetsMapByCustomer[customerIdToUpdate] || {};
    const existingPin = currentCustomerMap[assetId];
    const isOnPinnedTab = activeTab === 'pinned';
    const isPinnedInMap = Boolean(existingPin || pinnedAssetsMap[assetId]);
    const shouldUnpin = isOnPinnedTab || isPinnedInMap;

    setPinningAssetId(assetId);

    try {
      // ✅ Get current user ID for the pin payload
      const currentUserId = getCurrentUserId();

      // ✅ Use the Redux thunk with simplified PinPayload structure
      const result = await dispatch(pinAsset({
        assetId: assetId,
        isPinned: !shouldUnpin
      })).unwrap();

      // ✅ Update local state based on the result
      const updatedMap = { ...currentCustomerMap };

      if (shouldUnpin) {
        delete updatedMap[assetId];
        toast.success('Unpinned Successfully');
        setPinnedPageItems((prev) => prev.filter((item) => (item.assetId || item.id) !== assetId));
      } else {
        updatedMap[assetId] = {
          ...asset,
          assetId,
          pinnedAssetId: assetId,
        };
        toast.success('Pinned Successfully');
      }

      //  Update Redux store (localStorage persistence is handled in the action)
      dispatch(setPinnedAssetsMapByCustomer({
        customerId: customerIdToUpdate,
        pinnedAssets: updatedMap,
      }));

      pinnedPageCacheRef.current = {};
      await syncPinnedFromApi(
        selectedCustomerId || undefined,
        selectedSiteId || undefined,
        selectedPlantId || undefined
      );

    } catch (error) {
      toast.error('Failed to update pin status');
    } finally {
      setPinningAssetId(null);
    }
  };




  const handleAssetNameClick = (asset: any) => {
    const params = new URLSearchParams();
    if (asset.customerId) params.append('customerId', asset.customerId);
    if (asset.siteId) params.append('siteId', asset.siteId);
    if (asset.plantId) params.append('plantId', asset.plantId);
    if (asset.assetId) params.append('selectedAssetId', asset.assetId);

    const queryString = params.toString();
    const url = queryString ? `/asset-details/${asset.assetId}?${queryString}` : `/asset-details/${asset.assetId}`;
    navigate(url, {
      state: {
        assetName: asset.assetName,
        breadcrumbTitle: asset.assetName
      }
    });
  };
  const handleTabChange = (tab: 'assets' | 'pinned' | 'mapview') => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset pagination when switching tabs
  };
 

  //         'Accept': 'text/csv'
  //     link.href = url;

  //       filename += `_${customer?.customerName || selectedCustomerId}`;
  //       filename += `_${site?.site || selectedSiteId}`;
  //       filename += `_${plant?.plantName || selectedPlantId}`;
  //     filename += '.csv';

  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();

  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);

  // Display logic based on active tab
  const handleDownloadCSV = () =>
    downloadAssetsCsv({
      activeTab,
      selectedCustomerId,
      selectedSiteId,
      selectedPlantId,
      totalCount,
      pinnedTotalCount,
      customers,
      sites,
      plants,
    });

  const isDownloadDisabled = isSummaryDownloadDisabled(
    selectedCustomerId,
    activeTab,
    totalCount,
    pinnedTotalCount
  );

  // Count logic for tabs
  const assetsCount = React.useMemo(() => {
    return totalCount;
  }, [totalCount]);

  const pinnedAssetsCount = React.useMemo(() => {
    const mapCount = countPinnedAssetsInMap(
      pinnedAssetsMapByCustomer,
      selectedCustomerId || undefined
    );
    return Math.max(pinnedTotalCount, mapCount);
  }, [pinnedTotalCount, pinnedAssetsMapByCustomer, selectedCustomerId]);

  // localStorage persistence is now handled in the Redux action setPinnedAssetsMapByCustomer

  // Handle ESC key and outside click for modals
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showAlertsModal) closeAlertsModal();
      if (showGatewayStatusModal) closeGatewayStatusModal();
    };

    if (showAlertsModal || showGatewayStatusModal) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset'; // Restore scroll
    };
  }, [showAlertsModal, showGatewayStatusModal]);

  return {
    customers,
    extractedSites,
    extractedPlants,
    assetTypes,
    selectedCustomerId,
    setSelectedCustomerId,
    setSelectedSiteId,
    setSelectedPlantId,
    selectedSiteId,
    selectedPlantId,
    selectedAssetType,
    setSelectedAssetType,
    showAlertsOnly,
    handleAlertsToggleChange,
    activeTab,
    handleTabChange,
    searchText,
    handleSearchChange,
    handleDownloadCSV,
    isDownloadDisabled,
    loading,
    pinnedPageLoading,
    pagedAssetSummary,
    pagedPinnedRows,
    pinnedAssetsMap,
    pinningAssetId,
    sortConfig,
    pinnedSortConfig,
    requestSort,
    handlePinClick,
    handleAssetNameClick,
    handleGatewayStatusClick,
    handleAlertsClick,
    assetsCount,
    pinnedAssetsCount,
    currentPage,
    itemsPerPage,
    totalItems,
    totalPages,
    goToPreviousPage,
    goToNextPage,
    showGatewayStatusModal,
    selectedGatewayStatus,
    closeGatewayStatusModal,
    showAlertsModal,
    selectedAssetAlerts,
    closeAlertsModal,
    handleSiteChange,
    handlePlantChange,
    resetToFirstPage: () => setCurrentPage(1),
  };
}

const Summary: React.FC = () => <SummaryPageView {...useSummaryPage()} />;

export default Summary;
